import type { User } from '@auth0/auth0-vue'
import { Section, SectionColumn, type Similarities } from 'common'
import type { Callback, Dict, RequestOptions } from 'mixpanel-browser'
import mixpanel from 'mixpanel-browser'
import type { App } from 'vue'

import { useStripeStore } from '@/stores/stripe'
import queryBuilderTracking from '@/utils/queryBuilderTracking.ts'

let setup = false
const debug = false

/**
 * This function is used as a wrapper to ensure that mixpanel has been
 * initialized before forwarding the call to `mixpanel.track`.
 */
const track = (
  event_name: string,
  properties?: Dict,
  optionsOrCallback?: RequestOptions | Callback,
  callback?: Callback
) => {
  // Skip any tracking in the reports mode
  if (import.meta.env.MODE === 'reports') {
    return
  }

  if (!setup) {
    const mixpanelToken = import.meta.env.VITE_MIXPANEL_TOKEN as string
    if (mixpanelToken) {
      mixpanel.init(mixpanelToken, {
        debug: import.meta.env.MODE === 'development',
        persistence: 'localStorage',
        api_host: 'https://app.hellodata.ai/feedback/mp'
      })
      console.log('Mixpanel initialized')
    } else {
      console.log('Mixpanel not initialized, no token provided.')
    }

    setup = true
  }
  if (debug) {
    let humanizedProperties = ''
    if (properties) {
      for (const property in properties) {
        humanizedProperties += property + ': ' + properties[property] + '; '
      }
    }
    console.log(
      `Event: ${event_name}, params: ${humanizedProperties === '' ? 'none' : humanizedProperties}`
    )
  }

  mixpanel.track(event_name, properties, optionsOrCallback, callback)
}

const signedIn = (user: User, organizationName?: string, organizationId?: number) => {
  const stripeStore = useStripeStore()
  mixpanel.identify(user.sub)
  mixpanel.people.set({
    $email: user.email,
    $name: user.name,
    organization_id: organizationId,
    organization_name: organizationName,
    job_title: user.jobTitle ?? null
  })
  mixpanel.register({
    has_limited_access: stripeStore.hasLimitedAccess,
    has_property_reports: stripeStore.hasPropertyReports,
    has_market_reports: stripeStore.hasMarketReports,
    has_bulk_data: stripeStore.hasBulkData,
    trial_days_left: stripeStore.trialDaysLeft
  })
  track('signed_in')
}

const setSubscriptions = (subscriptions: string[]) => {
  mixpanel.people.set({ Subscriptions: subscriptions })
}

const usedCoupon = (coupon: string) => {
  mixpanel.people.set({ Coupon: coupon })
}

type FilterLocations =
  | 'bubble_chart'
  | 'concessions'
  | 'dashboard'
  | 'future_availability'
  | 'market_activity'
  | 'market_filters'
  | 'market_rent_comparison'
  | 'occupancy_trends'
  | 'price_recommendation'
  | 'property_rankings'
  | 'query_builder'
  | 'rent_comparison'
  | 'rent_comps'
  | 'rent_trends'
  | 'the_grid'
  | 'toolbar'
  | 'unit_details'
  | 'unit_mix'
  | 'unit_prices_over_time'
  | 'unit_types'
  | 'rent_projections'

type FilterNames =
  | 'amenities'
  | 'availability'
  | 'bubble_size'
  | 'building_rows'
  | 'color_by'
  | 'concession_status'
  | 'date_range'
  | 'display'
  | 'group_by'
  | 'lease_term'
  | 'market_amenities'
  | 'market_availability'
  | 'market_date_range'
  | 'market_unit_types'
  | 'metadata'
  | 'metric'
  | 'frequency'
  | 'properties'
  | 'rent_type'
  | 'sqft_range'
  | 'unit_rows'
  | 'unit_types'

const changedFilter = (name: FilterNames, location: FilterLocations, value: unknown) => {
  track('changed_filter', {
    name: name,
    location: location,
    value: value
  })
}

const setDefaultDateRange = (name: FilterNames, location: FilterLocations, value: string) => {
  track('set_default_date_range', {
    name: name,
    location: location,
    value: value
  })
}

const changedSort = (location: FilterLocations, columnName: string) => {
  track('changed_sort', {
    column_name: columnName,
    location: location
  })
}

const changedReportVisibility = (
  reportId: string | number,
  type: 'property' | 'market',
  scope: 'private' | 'public' | 'organization' | 'email' | 'removed',
  location: 'property_report' | 'market_report' | 'dashboard'
) => {
  track('shared_report', {
    report_type: type,
    report_id: reportId,
    scope: scope,
    location: location
  })
}

const changedTab = (
  fromTab: string,
  toTab: string,
  location:
    | 'property_report'
    | 'market_report'
    | 'settings'
    | 'organization'
    | 'property_report_settings'
) => {
  track('changed_tab', {
    from_tab: fromTab,
    to_tab: toTab,
    location: location
  })
}

const exported = (
  type:
    | 'bubble_chart'
    | 'dashboard'
    | 'excel'
    | 'exposure_trend'
    | 'future_availability'
    | 'market_activity_grid'
    | 'market_activity'
    | 'pdf'
    | 'legacy_pdf'
    | 'price_recommendation'
    | 'rent_trend_unit_details'
    | 'rent_trend'
    | 'unit_details'
    | 'unit_mix'
    | 'unit_mix_unit_details'
    | 'unit_type_details'
    | 'unit_prices_over_time'
    | 'overview_grid'
    | 'lease_terms',
  exportedSelection?: string[]
) => {
  track('exported', {
    type: type,
    exported_selection: exportedSelection
  })
}

const requestedComps = (customSettings?: Dict) => {
  track('requested_comps', {
    custom_settings: customSettings
  })
}

const excludedComp = (similarities?: Similarities) => {
  track('excluded_comps', {
    similarities
  })
}

const createdReport = (reportId: number, method: 'copy' | 'new', type: 'property' | 'market') => {
  track('created_report', {
    report_type: type,
    report_id: reportId,
    method
  })
}

const updatedReport = (reportId: number, type: 'property' | 'market', changed?: 'all' | 'tags') => {
  const changedVal = changed ? changed : 'all'
  track('updated_report', {
    report_type: type,
    report_id: reportId,
    changed_value: changedVal
  })
}

const deletedReport = (reportId: number, type: 'property' | 'market') => {
  track('deleted_report', {
    report_type: type,
    report_id: reportId
  })
}

const openedPopup = (popupName: string, location?: FilterLocations) => {
  track('opened_popup', {
    name: popupName,
    location: location
  })
}

const subscribed = (product: string, coupon?: string) => {
  track('subscribed', {
    product,
    coupon
  })
}

const updatedSeats = (product: string, oldSeats: number, newSeats: number) => {
  track('updated_seats', {
    product,
    old_seat_count: oldSeats,
    new_seat_count: newSeats
  })
}

const signedOut = () => {
  track('signed_out')
  mixpanel.reset()
}

const receivedInvitation = (accepted: boolean) => {
  track('received_invitation', {
    accepted
  })
}

const openedIntercom = () => {
  track('opened_intercom')
}

const toggledSidebar = (open: boolean) => {
  track('toggled_sidebar', {
    open
  })
}

const changedPricingSettings = (settings?: Dict) => {
  track('changed_pricing_settings', settings)
}

const selectedSearchedAddress = (address: string, optionIdx: number) => {
  track('searched_address', {
    address,
    selected_option: optionIdx
  })
}

const failedSearch = (address: string) => {
  track('failed_search', {
    address
  })
}

const clickedOnGraph = (
  graph: 'rent_trend' | 'market_activity' | 'future_availability' | 'bubble_chart'
) => {
  track('clicked_on_graph', {
    graph
  })
}

const changedAmenitySelection = (
  operation: 'added' | 'removed',
  level: 'unit' | 'building',
  amenity: string
) => {
  track('changed_amenity_selection', {
    operation,
    level,
    amenity
  })
}

const changedGridRowSelection = (
  operation: 'added' | 'removed',
  level: 'unit' | 'building',
  row_name: string
) => {
  track('changed_grid_row_selection', {
    operation,
    level,
    row_name
  })
}

const pageView = (page: string, path?: string) => {
  // leave path undefined if it's on another domain
  track('page_view', {
    name: page,
    path: path
  })
}

const usedRecommendedAmenities = (level: 'unit' | 'building') => {
  track('used_recommended_amenities', {
    level,
    location: 'new_property'
  })
}

const usedRecommendedUnitMix = () => {
  track('used_recommended_unit_mix', {
    location: 'new_property'
  })
}

const bulkImportedCSV = (count: number) => {
  track('bulk_imported_csv', {
    numberNewReports: count
  })
}

const updatedLogo = () => {
  track('updated_logo')
}

const movedComp = (compIdx: number, position: 'up' | 'down') => {
  track('moved_comp', {
    comp_index: compIdx,
    position
  })
}

const changedGPR = (propertyID: string, before: number, after: number) => {
  track('changed_gpr', {
    property_id: propertyID,
    before,
    after
  })
}

const generateReportButtonClicked = (location: string) => {
  track('generate_report_button_clicked', {
    location
  })
}

const version = (commitHash: string, commitTimestamp: string) => {
  track('version', {
    commit_hash: commitHash,
    commit_timestamp: commitTimestamp
  })
}

const changedVisibleSections = (sections: Section[], groupId: number) => {
  track('visible_sections', {
    sections: sections,
    group_id: groupId
  })
}

const changedVisibleColumns = (columns: SectionColumn[], groupId: number) => {
  track('visible_columns', {
    columns: columns,
    group_id: groupId
  })
}

const savedGroupPermissions = (groupName: string, permissions: string[]) => {
  track('saved_group_permissions', {
    group_name: groupName,
    permissions: permissions
  })
}

const expandedRow = (rowName: string, location: FilterLocations) => {
  track('expanded_row', {
    row: rowName,
    location: location
  })
}

const addCompsButtonClicked = () => {
  track('add_comps_button_clicked')
}

const skippedSubjectEntry = () => {
  track('skipped_subject_entry')
}

const unitMixCsvImport = () => {
  track('unit_mix_csv_import')
}

const expandedTrendSparkline = () => {
  track('expanded_trend_sparkline')
}

const organizationMemberRemoval = (userDataTransferred: boolean) => {
  track('report_transfer_on_organization_member_removal', { userDataTransferred })
}

const createdGroup = (groupName: string) => {
  track('added_group', {
    group_name: groupName
  })
}

const deletedGroup = (groupId: number, groupName: string) => {
  track('deleted_group', {
    group_id: groupId,
    group_name: groupName
  })
}

const renamedGroup = () => {
  track('renamed_group')
}

const createdDefaultGroup = () => {
  track('created_default_group')
}

const createNewFee = () => {
  track('created_new_fee')
}

const movedUsersToGroup = (userIds: string[], groupId: number) => {
  track('moved_users_to_group', {
    user_ids: userIds,
    group_id: groupId
  })
}

const addedUsersToGroup = (userIds: string[], groupId: number) => {
  track('added_users_to_group', {
    user_ids: userIds,
    group_id: groupId
  })
}

const removedUserFromGroup = (groupId: number, userId: string, userEmail: string) => {
  track('removed_user_from_group', {
    group_id: groupId,
    user_id: userId,
    user_email: userEmail
  })
}

const markedGroupAsExternal = () => {
  track('marked_group_as_external')
}

const clickedNewReportButton = (
  buttonPlacement: 'top_right_corner' | 'sidebar' | 'in_table_empty_state'
) => {
  track('clicked_new_report_button', {
    placement: buttonPlacement
  })
}

const openedGenerateReportDialog = () => {
  track('opened_generate_report_dialog')
}

const generatedReportFromDialog = (templateId: string, templateName: string) => {
  track('generated_report_from_dialog', {
    template_id: templateId,
    template_name: templateName
  })
}

const newVersionNotificationShown = () => {
  track('new_version_notification_shown')
}

const ownerDataBtnClicked = (propertyID: string) => {
  track('owner_data_btn_clicked', {
    property_id: propertyID
  })
}

const selectedShapeLayerPolygon = (
  type: null | 'MSA' | 'City' | 'County' | 'Zip Code' | 'Neighborhood'
) => {
  track('selected_shape_layer_polygon', {
    type
  })
}

export const tracker = {
  track,
  addCompsButtonClicked,
  addedUsersToGroup,
  bulkImportedCSV,
  changedAmenitySelection,
  changedFilter,
  changedGPR,
  changedGridRowSelection,
  changedPricingSettings,
  changedReportVisibility,
  changedSort,
  changedTab,
  changedVisibleColumns,
  changedVisibleSections,
  clickedNewReportButton,
  clickedOnGraph,
  createdDefaultGroup,
  createdGroup,
  createNewFee,
  createdReport,
  deletedGroup,
  deletedReport,
  excludedComp,
  expandedRow,
  expandedTrendSparkline,
  exported,
  failedSearch,
  generateReportButtonClicked,
  generatedReportFromDialog,
  markedGroupAsExternal,
  movedComp,
  movedUsersToGroup,
  newVersionNotificationShown,
  openedGenerateReportDialog,
  openedIntercom,
  openedPopup,
  organizationMemberRemoval,
  pageView,
  receivedInvitation,
  removedUserFromGroup,
  renamedGroup,
  requestedComps,
  savedGroupPermissions,
  selectedSearchedAddress,
  setDefaultDateRange,
  setSubscriptions,
  signedIn,
  signedOut,
  skippedSubjectEntry,
  subscribed,
  ownerDataBtnClicked,
  toggledSidebar,
  unitMixCsvImport,
  updatedLogo,
  updatedReport,
  updatedSeats,
  usedCoupon,
  usedRecommendedAmenities,
  usedRecommendedUnitMix,
  version,
  selectedShapeLayerPolygon,
  ...queryBuilderTracking
}

export type Tracker = typeof tracker

export default {
  install: (app: App) => {
    app.config.globalProperties.$tracker = tracker
  }
}
